import {
  Page,
  Text,
  LoginForm,
  Stack,
  Button,
  TextButton,
} from "@towersystems/roam-common-native";
import { View } from "react-native-ui-lib";
import packageInfo from "../../../../../package.json";
import styles from "./styles";
import { Logo } from "../../../../components/Logo/Logo";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useFormContext } from "react-hook-form";
import React from "react";
import { LoginFormValues } from "@towersystems/roam-common-native/lib/components/LoginForm/types";
import { MfaCodeStrategy } from "@towersystems/roam-common/lib/generated-types";
import { LoginFormAdditionalActionsContext } from "@towersystems/roam-common-native/lib/components/LoginForm/context";
import { Entypo } from "@expo/vector-icons";
import Constants from "expo-constants";
import { useEurekaLogin } from "../../../../utilities/eureka";
import { AnimatedDots } from "../../../../components/AnimatedDots";

const LoginTypesMap = {
  eureka: "Tower Advantage™ Login",
  talink: "Sign in with TA-Link",
  staff: "Sign in with Staff Account",
};

export const EUREKA_ENABLED =
  Constants.expoConfig?.extra?.eurekaEnabled ?? false;

  export const TALINK_ENABLED =
  Constants.expoConfig?.extra?.talinkEnabled ?? false;

  
const Login = () => {
  const { login, requestMfaCode } = useAuthenticationProvider();
  const formContext = useFormContext<LoginFormValues>();
  const { setValue, setError } = formContext;
  const { login: loginWithEureka } = useEurekaLogin();

  const [selectedLoginType, setSelectedLoginType] = React.useState<
    keyof typeof LoginTypesMap | undefined
  >(undefined);

  const onSubmit = React.useCallback(
    async (values: LoginFormValues) => {
      if (values.mfaRequired && values.mfaCodeSent == false) {
        return requestMfaCode(
          {
            strategy: values.strategy,
            talinkUsername: values.talinkUsername,
            staffUsername: values.staffUsername,
            password: values.password,
            rememberMe: values.rememberMe,
          },
          values.mfaStrategy as MfaCodeStrategy,
        )
          .then(() => {
            setValue("mfaCodeSent", true);
          })
          .catch((_err) => {
            setError("root.serverError", {
              type: "400",
              message: "Something went wrong.",
            });
          });
      }

      try {
        const result = await login({
          strategy: values.strategy,
          talinkUsername: values.talinkUsername,
          staffUsername: values.staffUsername,
          password: values.password,
          rememberMe: values.rememberMe,
          mfaCode: values.mfaCode,
          mfaStrategy: values.mfaStrategy,
        });
        if ("errorCode" in result) {
          if (
            result.errorCode === "MFA_REQUIRED_ERROR" &&
            (result as any).strategies
          ) {
            setValue("mfaRequired", true);
            setValue("mfaStrategies", (result as any).strategies);
            return;
          }

          if (result.errorCode === "INVALID_CREDENTIALS_ERROR") {
            setError("talinkUsername", {
              type: "a",
              message: "Invalid username or password",
            });
          } else {
            setError("root.serverError", {
              type: "400",
              message: result.errorCode,
            });
          }
        }
      } catch (error) {
        setError("root.serverError", {
          type: "400",
          message: "Something went wrong.",
        });
      }
    },
    [login, setError],
  );


  const loginTypeMarkup = (
    <Stack vertical>
      {EUREKA_ENABLED && (
        <>
          <Button
            label={LoginTypesMap.eureka}
            onPress={() => {
              setSelectedLoginType("eureka");
              loginWithEureka();
            }}
            fullWidth
          />
          <View style={styles.OrDivider}>
            <View style={styles.OrDividerLine} />
            <Text style={styles.OrDividerText}>or</Text>
            <View style={styles.OrDividerLine} />
          </View>
        </>
      )}
      {Object.entries(LoginTypesMap)
        .filter(([key]) => key !== "eureka" && (key !== "talink" || TALINK_ENABLED))
        .map(([key, label]) => (
          <Button
            key={key}
            label={label}
            onPress={() => {
              setSelectedLoginType(key as keyof typeof LoginTypesMap);
              if (key === "talink") {
                setValue("strategy", "talink");
              } else if (key === "staff") {
                setValue("strategy", "native");
              }
            }}
            variant="outline"
            fullWidth
          />
        ))}
    </Stack>
  );


  if (!selectedLoginType) {
    return <View style={{ marginTop: 24 }}>{loginTypeMarkup}</View>;
  }

  return (
    <View>
      <View style={{ marginTop: 24 }}>
        <TextButton
          onPress={() => setSelectedLoginType(undefined)}
          style={{ marginBottom: 16 }}
          iconPosition="left"
          icon={<Entypo name="chevron-thin-left" size={20} />}
        >
          Go back to login options
        </TextButton>
      </View>
      {["talink", "staff"].includes(selectedLoginType) && (
        <LoginFormAdditionalActionsContext.Provider
          value={{
            onSubmitEditingAll: formContext.handleSubmit(onSubmit),
          }}
        >
          <LoginForm onSubmit={onSubmit} talinkEnabled={false}/>
        </LoginFormAdditionalActionsContext.Provider>
      )}
      {selectedLoginType === "eureka" && (
        <View style={{ marginTop: 24 }}>
          <AnimatedDots text="You will be redirected to Eureka login soon." />
        </View>
      )}
    </View>
  );
};

export const LogInScreen = () => {
  return (
    <Page padding={0} fullWidth scrollable={false}>
      <View style={[styles.ContainerWrapper]}>
        <View style={[styles.Container]}>
          <View style={[styles.ContainerInner]}>
            <View
              style={[
                styles.LoginPageMain,
                {
                  width: "100%",
                },
              ]}
            >
              <View style={styles.LogoWrapper}>
                <Logo />
                <View style={styles.VersionBadge}>
                  <Text variant="caption">{`V.${packageInfo.version}`}</Text>
                </View>
              </View>
              <LoginForm.Provider>
                <Login />
              </LoginForm.Provider>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.Footer}>
        <Text variant="caption">{`© ${new Date().getFullYear()} Tower Systems Pty Ltd · Roam Utilities`}</Text>
        <Text variant="caption">{`V.${packageInfo.version}`}</Text>
      </View>
    </Page>
  );
};
